import * as Sentry from "@sentry/solidstart";

import Log from "@repo/utils/Log";
import UObject from "@repo/utils/UObject";

namespace USentry {
  export const initSolidStart = ({
    beforeSend,
    apiOrigin,
    ...options
  }: UObject.Override<
    Parameters<typeof Sentry.init>[0],
    {
      apiOrigin: string;
      beforeSend?: (context: {
        event: Sentry.ErrorEvent;
        hint: Sentry.EventHint;
        suppress: boolean;
        frames: Sentry.StackFrame[] | undefined;
      }) => ReturnType<NonNullable<Sentry.BrowserOptions["beforeSend"]>>;
    }
  >) =>
    Sentry.init({
      integrations: [Sentry.replayIntegration()],
      // Performance Monitoring
      tracesSampleRate: 1.0,
      // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
      tracePropagationTargets: [apiOrigin],
      // Session Replays
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 1.0,
      beforeSend(event, hint) {
        const frames = event.exception?.values?.[0]?.stacktrace?.frames;

        const suppress =
          // Suppress events without errors, we can't do much with these anyways
          !frames?.some(
            ({ filename }) =>
              filename &&
              /^https?:\/\//.test(filename) &&
              // Ignore errors from entry html file, we don't have application code
              // there, but browsers can throw with this source
              /\.(js|ts|jsx|tsx|mts|mjs)$/.test(new URL(filename).pathname),
          ) ||
          // Suppress errors from 3rd party scripts
          frames.some(
            ({ filename }) =>
              filename?.startsWith("https://") &&
              !filename.startsWith(window.location.origin),
          );

        const res = (() => {
          if (beforeSend) return beforeSend({ event, hint, suppress, frames });
          if (suppress) return null;
          return event;
        })();

        Log.withTrace`${Log.color("#f20", "Sentry event")} ${!res ? Log.style("background:green", " Suppressed ") : ""} -> ${event}`;

        return res;
      },
      ...options,
    });
}

export default USentry;
